import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Users, User, CheckCircle, XCircle, Clock, Eye, AlertTriangle, X,
  Search, Filter, Building, Mail, Calendar, Hash, Shield,
  UserX, UserCheck, Archive, ChevronRight, ChevronDown, Store,
  Activity, AlertCircle, RefreshCw, ArrowLeft, ChevronLeft
} from 'lucide-react';
import './TeamMembersManagement.css';

// ─────────────────────────────────────────────
// Toast notification (replaces browser alert)
// ─────────────────────────────────────────────
const Toast = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 4000);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div className={`tm-toast tm-toast-${type}`}>
      {type === 'success' ? <CheckCircle size={18} /> : <AlertCircle size={18} />}
      <span>{message}</span>
      <button className="tm-toast-close" onClick={onClose}><X size={14} /></button>
    </div>
  );
};

// ─────────────────────────────────────────────
// Reason Modal (replaces browser prompt)
// ─────────────────────────────────────────────
const ReasonModal = ({ title, label, placeholder, confirmText, confirmStyle, onConfirm, onCancel }) => {
  const [reason, setReason] = useState('');
  const textareaRef = useRef(null);

  useEffect(() => {
    textareaRef.current?.focus();
  }, []);

  return (
    <div className="tm-modal-overlay" onClick={onCancel}>
      <div className="tm-modal-box" onClick={(e) => e.stopPropagation()}>
        <div className="tm-modal-header">
          <h3 className="tm-modal-title">{title}</h3>
          <button className="tm-modal-close" onClick={onCancel}><X size={16} /></button>
        </div>
        <div className="tm-modal-body">
          <label>{label}</label>
          <textarea
            ref={textareaRef}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={placeholder}
          />
        </div>
        <div className="tm-modal-footer">
          <button className="tm-modal-cancel-btn" onClick={onCancel}>Cancel</button>
          <button
            className={`tm-modal-confirm-btn ${confirmStyle}`}
            disabled={!reason.trim()}
            onClick={() => onConfirm(reason.trim())}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────
// Confirm Modal (replaces browser confirm)
// ─────────────────────────────────────────────
const ConfirmModal = ({ title, message, confirmText, confirmStyle, onConfirm, onCancel }) => {
  return (
    <div className="tm-modal-overlay" onClick={onCancel}>
      <div className="tm-modal-box" onClick={(e) => e.stopPropagation()}>
        <div className="tm-modal-header">
          <h3 className="tm-modal-title">{title}</h3>
          <button className="tm-modal-close" onClick={onCancel}><X size={16} /></button>
        </div>
        <div className="tm-modal-body">
          <p style={{ margin: 0, fontSize: 14, color: '#475569', lineHeight: 1.6 }}>{message}</p>
        </div>
        <div className="tm-modal-footer">
          <button className="tm-modal-cancel-btn" onClick={onCancel}>Cancel</button>
          <button className={`tm-modal-confirm-btn ${confirmStyle}`} onClick={onConfirm}>
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════
// Main Component
// ═══════════════════════════════════════════════
const TeamMembersManagement = () => {
  // Stores (merchants) state
  const [stores, setStores] = useState([]);
  const [storesLoading, setStoresLoading] = useState(true);
  const [storeSearch, setStoreSearch] = useState('');
  const [storesPagination, setStoresPagination] = useState(null);
  const [storesPage, setStoresPage] = useState(1);

  // Selected store and its team members
  const [selectedStore, setSelectedStore] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [memberSearch, setMemberSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterRole, setFilterRole] = useState('all');

  // Flyout state
  const [selectedMember, setSelectedMember] = useState(null);
  const [flyoutData, setFlyoutData] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  // Global stats
  const [globalStats, setGlobalStats] = useState(null);

  // UI state
  const [showFilters, setShowFilters] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [toast, setToast] = useState(null);
  const [reasonModal, setReasonModal] = useState(null);
  const [confirmModal, setConfirmModal] = useState(null);

  // Search debounce refs
  const storeSearchTimeout = useRef(null);
  const memberSearchTimeout = useRef(null);

  const showToast = useCallback((message, type = 'success') => {
    setToast({ message, type });
  }, []);

  const dismissToast = useCallback(() => {
    setToast(null);
  }, []);

  // ───── Fetch Functions ─────

  const fetchGlobalStats = useCallback(async () => {
    try {
      const response = await fetch('/api/admin/team-members/stats', { credentials: 'include' });
      const data = await response.json();
      if (data.success) {
        setGlobalStats(data.stats);
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  }, []);

  const fetchStores = useCallback(async () => {
    try {
      setStoresLoading(true);
      const params = new URLSearchParams({
        page: storesPage,
        limit: 30
      });
      if (storeSearch) params.append('search', storeSearch);

      const response = await fetch(`/api/admin/team-members/stores?${params}`, { credentials: 'include' });
      const data = await response.json();
      if (data.success) {
        setStores(data.stores);
        setStoresPagination(data.pagination);
      }
    } catch (error) {
      console.error('Failed to fetch stores:', error);
    } finally {
      setStoresLoading(false);
    }
  }, [storeSearch, storesPage]);

  const fetchTeamMembers = useCallback(async () => {
    if (!selectedStore) return;
    try {
      setMembersLoading(true);
      const params = new URLSearchParams({
        storeId: selectedStore.id,
        limit: 50
      });
      if (filterStatus !== 'all') params.append('status', filterStatus);
      if (filterRole !== 'all') params.append('role', filterRole);
      if (memberSearch) params.append('search', memberSearch);

      const response = await fetch(`/api/admin/team-members?${params}`, { credentials: 'include' });
      const data = await response.json();
      if (data.success) {
        setTeamMembers(data.teamMembers);
      }
    } catch (error) {
      console.error('Failed to fetch team members:', error);
    } finally {
      setMembersLoading(false);
    }
  }, [selectedStore, filterStatus, filterRole, memberSearch]);

  useEffect(() => {
    fetchStores();
    fetchGlobalStats();
  }, [fetchStores, fetchGlobalStats]);

  useEffect(() => {
    if (selectedStore) {
      fetchTeamMembers();
    }
  }, [selectedStore, fetchTeamMembers]);

  // ───── Navigation ─────

  const selectStore = (store) => {
    setSelectedStore(store);
    setTeamMembers([]);
    setMemberSearch('');
    setFilterStatus('all');
    setFilterRole('all');
  };

  const backToStores = () => {
    setSelectedStore(null);
    setTeamMembers([]);
  };

  const viewMemberDetails = async (memberId) => {
    try {
      setSelectedMember(memberId);
      const response = await fetch(`/api/admin/team-members/${memberId}`, { credentials: 'include' });
      const data = await response.json();
      if (data.success) {
        setFlyoutData(data);
      }
    } catch (error) {
      console.error('Failed to fetch member details:', error);
    }
  };

  const closeFlyout = () => {
    setSelectedMember(null);
    setFlyoutData(null);
  };

  // ───── Actions ─────

  const refreshAll = async () => {
    setRefreshing(true);
    await Promise.all([
      fetchStores(),
      fetchGlobalStats(),
      selectedStore ? fetchTeamMembers() : Promise.resolve()
    ]);
    setRefreshing(false);
  };

  const handleSuspend = () => {
    setReasonModal({
      title: 'Suspend Team Member',
      label: 'Reason for suspension',
      placeholder: 'Explain why this team member is being suspended...',
      confirmText: 'Suspend',
      confirmStyle: 'tm-modal-confirm-danger',
      onConfirm: async (reason) => {
        setReasonModal(null);
        try {
          setActionLoading(true);
          const response = await fetch(`/api/admin/team-members/${selectedMember}/suspend`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ reason })
          });
          const data = await response.json();
          if (data.success) {
            showToast('Team member suspended successfully.');
            closeFlyout();
            fetchTeamMembers();
            fetchStores();
            fetchGlobalStats();
          } else {
            showToast(data.error || 'Failed to suspend team member', 'error');
          }
        } catch (error) {
          console.error('Failed to suspend:', error);
          showToast('Error suspending team member', 'error');
        } finally {
          setActionLoading(false);
        }
      }
    });
  };

  const handleUnsuspend = () => {
    setConfirmModal({
      title: 'Unsuspend Team Member',
      message: 'Are you sure you want to unsuspend this team member? They will regain access to their account.',
      confirmText: 'Unsuspend',
      confirmStyle: 'tm-modal-confirm-warning',
      onConfirm: async () => {
        setConfirmModal(null);
        try {
          setActionLoading(true);
          const response = await fetch(`/api/admin/team-members/${selectedMember}/unsuspend`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include'
          });
          const data = await response.json();
          if (data.success) {
            showToast('Team member unsuspended successfully.');
            closeFlyout();
            fetchTeamMembers();
            fetchStores();
            fetchGlobalStats();
          } else {
            showToast(data.error || 'Failed to unsuspend team member', 'error');
          }
        } catch (error) {
          console.error('Failed to unsuspend:', error);
          showToast('Error unsuspending team member', 'error');
        } finally {
          setActionLoading(false);
        }
      }
    });
  };

  const handleArchive = () => {
    setReasonModal({
      title: 'Archive Team Member',
      label: 'Reason for archiving',
      placeholder: 'Explain why this team member is being archived...',
      confirmText: 'Archive',
      confirmStyle: 'tm-modal-confirm-warning',
      onConfirm: async (reason) => {
        setReasonModal(null);
        try {
          setActionLoading(true);
          const response = await fetch(`/api/admin/team-members/${selectedMember}/archive`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ reason })
          });
          const data = await response.json();
          if (data.success) {
            showToast('Team member archived successfully.');
            closeFlyout();
            fetchTeamMembers();
            fetchStores();
            fetchGlobalStats();
          } else {
            showToast(data.error || 'Failed to archive team member', 'error');
          }
        } catch (error) {
          console.error('Failed to archive:', error);
          showToast('Error archiving team member', 'error');
        } finally {
          setActionLoading(false);
        }
      }
    });
  };

  const handleUnarchive = () => {
    setConfirmModal({
      title: 'Unarchive Team Member',
      message: 'Are you sure you want to unarchive this team member? They will be able to log in again.',
      confirmText: 'Unarchive',
      confirmStyle: 'tm-modal-confirm-warning',
      onConfirm: async () => {
        setConfirmModal(null);
        try {
          setActionLoading(true);
          const response = await fetch(`/api/admin/team-members/${selectedMember}/unarchive`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include'
          });
          const data = await response.json();
          if (data.success) {
            showToast('Team member unarchived successfully.');
            closeFlyout();
            fetchTeamMembers();
            fetchStores();
            fetchGlobalStats();
          } else {
            showToast(data.error || 'Failed to unarchive team member', 'error');
          }
        } catch (error) {
          console.error('Failed to unarchive:', error);
          showToast('Error unarchiving team member', 'error');
        } finally {
          setActionLoading(false);
        }
      }
    });
  };

  // ───── Badge Helpers ─────

  const getStatusBadge = (status) => {
    const badges = {
      PENDING_INVITE: { color: '#f59e0b', bg: '#fffbeb', icon: Clock, text: 'Pending' },
      ACTIVE: { color: '#16a34a', bg: '#f0fdf4', icon: CheckCircle, text: 'Active' },
      SUSPENDED: { color: '#dc2626', bg: '#fef2f2', icon: UserX, text: 'Suspended' },
      ARCHIVED: { color: '#64748b', bg: '#f1f5f9', icon: Archive, text: 'Archived' }
    };
    const badge = badges[status] || badges.PENDING_INVITE;
    const Icon = badge.icon;
    return (
      <span className="tm-status-badge" style={{ backgroundColor: badge.bg, color: badge.color }}>
        <Icon size={12} />
        {badge.text}
      </span>
    );
  };

  const getRoleBadge = (roleName) => {
    const roleColors = {
      'Owner': { color: '#7c3aed', bg: '#f5f3ff' },
      'Admin': { color: '#2563eb', bg: '#eff6ff' },
      'Manager': { color: '#059669', bg: '#ecfdf5' },
      'Staff': { color: '#475569', bg: '#f1f5f9' },
      'Cashier': { color: '#d97706', bg: '#fffbeb' },
      'Inventory': { color: '#0891b2', bg: '#ecfeff' },
      'Sales': { color: '#e11d48', bg: '#fff1f2' },
      'Marketing': { color: '#9333ea', bg: '#faf5ff' },
      'Viewer': { color: '#64748b', bg: '#f8fafc' }
    };
    const config = roleColors[roleName] || { color: '#64748b', bg: '#f1f5f9' };
    return (
      <span className="tm-role-badge" style={{ backgroundColor: config.bg, color: config.color }}>
        <Shield size={10} />
        {roleName}
      </span>
    );
  };

  // ───── Formatters ─────

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatDateTime = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const timeAgo = (date) => {
    if (!date) return 'Never';
    const now = new Date();
    const past = new Date(date);
    const diffMs = now - past;
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHrs = Math.floor(diffMins / 60);
    if (diffHrs < 24) return `${diffHrs}h ago`;
    const diffDays = Math.floor(diffHrs / 24);
    if (diffDays < 7) return `${diffDays}d ago`;
    return formatDate(date);
  };

  const getInitials = (email) => {
    if (!email) return 'U';
    const parts = email.split('@')[0].split(/[._-]/);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return email.charAt(0).toUpperCase();
  };

  // ───── Debounced search handlers ─────

  const handleStoreSearchChange = (e) => {
    const value = e.target.value;
    setStoreSearch(value);
    setStoresPage(1);
  };

  const handleMemberSearchChange = (e) => {
    setMemberSearch(e.target.value);
  };

  const hasActiveFilters = filterStatus !== 'all' || filterRole !== 'all';

  const clearFilters = () => {
    setFilterStatus('all');
    setFilterRole('all');
  };

  // ═══════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════
  return (
    <div className="tm-management-container">
      {/* Toast */}
      {toast && <Toast message={toast.message} type={toast.type} onClose={dismissToast} />}

      {/* Reason Modal */}
      {reasonModal && (
        <ReasonModal
          title={reasonModal.title}
          label={reasonModal.label}
          placeholder={reasonModal.placeholder}
          confirmText={reasonModal.confirmText}
          confirmStyle={reasonModal.confirmStyle}
          onConfirm={reasonModal.onConfirm}
          onCancel={() => setReasonModal(null)}
        />
      )}

      {/* Confirm Modal */}
      {confirmModal && (
        <ConfirmModal
          title={confirmModal.title}
          message={confirmModal.message}
          confirmText={confirmModal.confirmText}
          confirmStyle={confirmModal.confirmStyle}
          onConfirm={confirmModal.onConfirm}
          onCancel={() => setConfirmModal(null)}
        />
      )}

      {/* ────── HEADER ────── */}
      <div className="tm-header">
        <div className="tm-header-left">
          <h1 className="tm-header-title">Team Members</h1>
          <p className="tm-header-subtitle">Manage merchant team members across the platform</p>
        </div>
        <div className="tm-header-actions">
          <div className="tm-search-box">
            <Search size={16} />
            <input
              type="text"
              placeholder="Search merchants..."
              value={storeSearch}
              onChange={handleStoreSearchChange}
            />
          </div>
          <button
            className={`tm-filter-toggle ${showFilters ? 'tm-active' : ''}`}
            onClick={() => setShowFilters(!showFilters)}
          >
            <Filter size={15} />
            Filters
            {hasActiveFilters && (
              <span style={{
                width: 6, height: 6, borderRadius: '50%',
                background: '#1a6f63', display: 'inline-block'
              }} />
            )}
          </button>
          <button
            className={`tm-refresh-btn ${refreshing ? 'tm-spinning' : ''}`}
            onClick={refreshAll}
            title="Refresh all data"
          >
            <RefreshCw size={16} />
          </button>
        </div>
      </div>

      {/* ────── FILTER BAR ────── */}
      {showFilters && (
        <div className="tm-filter-bar">
          <span className="tm-filter-bar-label">Filter by</span>

          <div className="tm-filter-select">
            <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
              <option value="all">All Status</option>
              <option value="ACTIVE">Active</option>
              <option value="PENDING_INVITE">Pending</option>
              <option value="SUSPENDED">Suspended</option>
              <option value="ARCHIVED">Archived</option>
            </select>
            <ChevronDown size={14} />
          </div>

          <div className="tm-filter-separator" />

          <div className="tm-filter-select">
            <select value={filterRole} onChange={(e) => setFilterRole(e.target.value)}>
              <option value="all">All Roles</option>
              <option value="OWNER">Owner</option>
              <option value="MANAGER">Manager</option>
              <option value="INVENTORY">Inventory</option>
              <option value="SALES">Sales</option>
              <option value="MARKETING">Marketing</option>
              <option value="CASHIER">Cashier</option>
              <option value="VIEWER">Viewer</option>
            </select>
            <ChevronDown size={14} />
          </div>

          {hasActiveFilters && (
            <button className="tm-filter-clear" onClick={clearFilters}>
              <X size={12} />
              Clear
            </button>
          )}
        </div>
      )}

      {/* ────── STAT CARDS ────── */}
      {globalStats && (
        <div className="tm-stats-grid">
          <div className="tm-stat-card tm-stat-total">
            <div className="tm-stat-icon"><Users size={20} /></div>
            <div className="tm-stat-value">{globalStats.total || 0}</div>
            <div className="tm-stat-label">Total Members</div>
          </div>
          <div className="tm-stat-card tm-stat-active">
            <div className="tm-stat-icon"><CheckCircle size={20} /></div>
            <div className="tm-stat-value">{globalStats.byStatus?.active || 0}</div>
            <div className="tm-stat-label">Active</div>
          </div>
          <div className="tm-stat-card tm-stat-pending">
            <div className="tm-stat-icon"><Clock size={20} /></div>
            <div className="tm-stat-value">{globalStats.byStatus?.pendingInvite || 0}</div>
            <div className="tm-stat-label">Pending</div>
          </div>
          <div className="tm-stat-card tm-stat-suspended">
            <div className="tm-stat-icon"><UserX size={20} /></div>
            <div className="tm-stat-value">{globalStats.byStatus?.suspended || 0}</div>
            <div className="tm-stat-label">Suspended</div>
          </div>
        </div>
      )}

      {/* ────── SPLIT LAYOUT ────── */}
      <div className="tm-content-area">
        {/* ── Left: Merchants ── */}
        <div className={`tm-merchants-panel ${selectedStore ? 'tm-collapsed' : ''}`}>
          <div className="tm-panel-header">
            <h2 className="tm-panel-header-title">Merchants</h2>
            <span className="tm-panel-count">{storesPagination?.total || 0} stores</span>
          </div>

          <div className="tm-panel-search">
            <Search size={16} />
            <input
              type="text"
              placeholder="Search merchants..."
              value={storeSearch}
              onChange={handleStoreSearchChange}
            />
          </div>

          <div className="tm-merchants-list">
            {storesLoading ? (
              <div className="tm-loading-state">
                <div className="tm-spinner" />
                <span>Loading merchants...</span>
              </div>
            ) : stores.length === 0 ? (
              <div className="tm-empty-state">
                <Store size={36} />
                <p className="tm-empty-text">No merchants with team members found</p>
              </div>
            ) : (
              stores.map((store) => (
                <div
                  key={store.id}
                  className={`tm-merchant-item ${selectedStore?.id === store.id ? 'tm-selected' : ''}`}
                  onClick={() => selectStore(store)}
                >
                  <div className="tm-merchant-avatar">
                    {store.logo ? (
                      <img src={store.logo} alt={store.name} />
                    ) : (
                      store.name.charAt(0).toUpperCase()
                    )}
                  </div>
                  <div className="tm-merchant-info">
                    <div className="tm-merchant-name">{store.name}</div>
                    <div className="tm-merchant-email">{store.owner?.email}</div>
                  </div>
                  <div className="tm-merchant-team-count">
                    <span className="tm-count-badge">{store.teamMemberCount}</span>
                    <div className="tm-count-breakdown">
                      {store.statusCounts?.active > 0 && (
                        <span className="tm-mini-badge tm-mini-active">{store.statusCounts.active}</span>
                      )}
                      {store.statusCounts?.pending > 0 && (
                        <span className="tm-mini-badge tm-mini-pending">{store.statusCounts.pending}</span>
                      )}
                      {store.statusCounts?.suspended > 0 && (
                        <span className="tm-mini-badge tm-mini-suspended">{store.statusCounts.suspended}</span>
                      )}
                    </div>
                  </div>
                  <ChevronRight size={16} className="tm-merchant-chevron" />
                </div>
              ))
            )}
          </div>

          {storesPagination && storesPagination.pages > 1 && (
            <div className="tm-pagination">
              <button
                className="tm-pagination-btn"
                disabled={storesPage === 1}
                onClick={() => setStoresPage(storesPage - 1)}
              >
                <ChevronLeft size={16} />
              </button>
              <span className="tm-pagination-info">
                {storesPage} of {storesPagination.pages}
              </span>
              <button
                className="tm-pagination-btn"
                disabled={storesPage === storesPagination.pages}
                onClick={() => setStoresPage(storesPage + 1)}
              >
                <ChevronRight size={16} />
              </button>
            </div>
          )}
        </div>

        {/* ── Right: Team Members ── */}
        <div className="tm-members-panel">
          {!selectedStore ? (
            <div className="tm-placeholder-state">
              <Users size={48} />
              <h3 className="tm-placeholder-title">Select a Merchant</h3>
              <p className="tm-placeholder-desc">
                Choose a merchant from the list to view and manage their team members
              </p>
            </div>
          ) : (
            <>
              {/* Header with back button */}
              <div className="tm-panel-header-with-back">
                <button className="tm-back-btn" onClick={backToStores}>
                  <ArrowLeft size={18} />
                </button>
                <div className="tm-selected-store-info">
                  <h2 className="tm-selected-store-name">{selectedStore.name}</h2>
                  <p className="tm-selected-store-email">{selectedStore.owner?.email}</p>
                </div>
                <button className="tm-panel-refresh" onClick={fetchTeamMembers}>
                  <RefreshCw size={15} />
                </button>
              </div>

              {/* Search + inline filter */}
              <div className="tm-panel-controls">
                <div className="tm-panel-search">
                  <Search size={15} />
                  <input
                    type="text"
                    placeholder="Search members..."
                    value={memberSearch}
                    onChange={handleMemberSearchChange}
                  />
                </div>
                <div className="tm-member-filter-select">
                  <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
                    <option value="all">All Status</option>
                    <option value="ACTIVE">Active</option>
                    <option value="PENDING_INVITE">Pending</option>
                    <option value="SUSPENDED">Suspended</option>
                    <option value="ARCHIVED">Archived</option>
                  </select>
                  <ChevronDown size={13} />
                </div>
              </div>

              {/* Members Table (desktop) */}
              {membersLoading ? (
                <div className="tm-loading-state">
                  <div className="tm-spinner" />
                  <span>Loading team members...</span>
                </div>
              ) : teamMembers.length === 0 ? (
                <div className="tm-empty-state">
                  <User size={36} />
                  <p className="tm-empty-text">No team members found</p>
                </div>
              ) : (
                <>
                  {/* Desktop Table */}
                  <div className="tm-members-table-wrap">
                    <table className="tm-members-table">
                      <thead>
                        <tr>
                          <th>Member</th>
                          <th>Role</th>
                          <th>Status</th>
                          <th>Last Active</th>
                          <th style={{ width: 40 }}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {teamMembers.map((member) => (
                          <tr key={member.id} onClick={() => viewMemberDetails(member.id)}>
                            <td>
                              <div className="tm-table-member-cell">
                                <div className="tm-table-avatar">
                                  {getInitials(member.user?.email)}
                                </div>
                                <div className="tm-table-member-info">
                                  <div className="tm-table-member-name">
                                    {member.user?.username || member.user?.email?.split('@')[0] || 'Unknown'}
                                  </div>
                                  <div className="tm-table-member-email">{member.user?.email}</div>
                                </div>
                              </div>
                            </td>
                            <td>{getRoleBadge(member.role?.name || 'Unknown')}</td>
                            <td>{getStatusBadge(member.status)}</td>
                            <td>
                              <span className="tm-table-last-active">
                                {timeAgo(member.lastActiveAt)}
                              </span>
                            </td>
                            <td>
                              <Eye size={16} className="tm-table-view-icon" />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Mobile Cards */}
                  <div className="tm-mobile-cards">
                    {teamMembers.map((member) => (
                      <div
                        key={member.id}
                        className="tm-mobile-card"
                        onClick={() => viewMemberDetails(member.id)}
                      >
                        <div className="tm-mobile-card-top">
                          <div className="tm-mobile-card-avatar">
                            {getInitials(member.user?.email)}
                          </div>
                          <div>
                            <div className="tm-mobile-card-name">
                              {member.user?.username || member.user?.email?.split('@')[0] || 'Unknown'}
                            </div>
                            <div className="tm-mobile-card-email">{member.user?.email}</div>
                          </div>
                        </div>
                        <div className="tm-mobile-card-bottom">
                          <div className="tm-mobile-card-badges">
                            {getRoleBadge(member.role?.name || 'Unknown')}
                            {getStatusBadge(member.status)}
                          </div>
                          <span className="tm-mobile-card-date">
                            {timeAgo(member.lastActiveAt)}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>

      {/* ────── FLYOUT ────── */}
      {selectedMember && flyoutData && (
        <TeamMemberFlyout
          data={flyoutData}
          onClose={closeFlyout}
          onSuspend={handleSuspend}
          onUnsuspend={handleUnsuspend}
          onArchive={handleArchive}
          onUnarchive={handleUnarchive}
          actionLoading={actionLoading}
          getInitials={getInitials}
          formatDateTime={formatDateTime}
        />
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════
// Flyout Component
// ═══════════════════════════════════════════════
const TeamMemberFlyout = ({
  data,
  onClose,
  onSuspend,
  onUnsuspend,
  onArchive,
  onUnarchive,
  actionLoading,
  getInitials,
  formatDateTime
}) => {
  const [activeTab, setActiveTab] = useState('overview');
  const { teamMember, activityLogs, merchant } = data;

  const getStatusBadge = (status) => {
    const badges = {
      PENDING_INVITE: { color: '#f59e0b', bg: '#fffbeb', text: 'Pending Invite' },
      ACTIVE: { color: '#16a34a', bg: '#f0fdf4', text: 'Active' },
      SUSPENDED: { color: '#dc2626', bg: '#fef2f2', text: 'Suspended' },
      ARCHIVED: { color: '#64748b', bg: '#f1f5f9', text: 'Archived' }
    };
    const badge = badges[status] || badges.PENDING_INVITE;
    return (
      <span className="tm-flyout-status-badge" style={{ backgroundColor: badge.bg, color: badge.color }}>
        {badge.text}
      </span>
    );
  };

  return (
    <>
      <div className="tm-flyout-overlay" onClick={onClose} />
      <div className="tm-flyout">
        {/* Header */}
        <div className="tm-flyout-header">
          <div className="tm-flyout-member-avatar">
            {getInitials(teamMember.user?.email)}
          </div>
          <div className="tm-flyout-header-info">
            <h2 className="tm-flyout-member-name">
              {teamMember.user?.username || teamMember.user?.email || 'Team Member'}
            </h2>
            <p className="tm-flyout-member-meta">
              <span>{teamMember.store?.name}</span>
              <span style={{ color: '#cbd5e1' }}>|</span>
              <span>{teamMember.role?.name}</span>
            </p>
          </div>
          <button className="tm-flyout-close-btn" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        {/* Tabs */}
        <div className="tm-flyout-tabs">
          <button
            className={`tm-flyout-tab ${activeTab === 'overview' ? 'tm-tab-active' : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            <User size={15} />
            Overview
          </button>
          <button
            className={`tm-flyout-tab ${activeTab === 'merchant' ? 'tm-tab-active' : ''}`}
            onClick={() => setActiveTab('merchant')}
          >
            <Building size={15} />
            Merchant
          </button>
          <button
            className={`tm-flyout-tab ${activeTab === 'activity' ? 'tm-tab-active' : ''}`}
            onClick={() => setActiveTab('activity')}
          >
            <Activity size={15} />
            Activity ({activityLogs?.length || 0})
          </button>
        </div>

        {/* Content */}
        <div className="tm-flyout-content">
          {activeTab === 'overview' && (
            <>
              <div className="tm-info-section">
                <h3 className="tm-info-section-title">
                  <User size={15} />
                  Member Details
                </h3>
                <div className="tm-detail-list">
                  <div className="tm-detail-row">
                    <span className="tm-detail-label">Email</span>
                    <span className="tm-detail-value">{teamMember.user?.email || 'N/A'}</span>
                  </div>
                  <div className="tm-detail-row">
                    <span className="tm-detail-label">Username</span>
                    <span className="tm-detail-value">@{teamMember.user?.username || 'N/A'}</span>
                  </div>
                  <div className="tm-detail-row">
                    <span className="tm-detail-label">Status</span>
                    {getStatusBadge(teamMember.status)}
                  </div>
                  <div className="tm-detail-row">
                    <span className="tm-detail-label">Role</span>
                    <span className="tm-detail-value">{teamMember.role?.name || 'N/A'}</span>
                  </div>
                  <div className="tm-detail-row">
                    <span className="tm-detail-label">Member ID</span>
                    <span className="tm-detail-value tm-monospace">
                      {teamMember.id?.substring(0, 8)}...
                    </span>
                  </div>
                </div>
              </div>

              <div className="tm-info-section">
                <h3 className="tm-info-section-title">
                  <Calendar size={15} />
                  Timeline
                </h3>
                <div className="tm-detail-list">
                  {teamMember.invitedAt && (
                    <div className="tm-detail-row">
                      <span className="tm-detail-label">Invited</span>
                      <span className="tm-detail-value">{formatDateTime(teamMember.invitedAt)}</span>
                    </div>
                  )}
                  <div className="tm-detail-row">
                    <span className="tm-detail-label">Joined</span>
                    <span className="tm-detail-value">
                      {teamMember.joinedAt
                        ? formatDateTime(teamMember.joinedAt)
                        : <span className="tm-muted-text">Not yet joined</span>}
                    </span>
                  </div>
                  <div className="tm-detail-row">
                    <span className="tm-detail-label">Last Active</span>
                    <span className="tm-detail-value">
                      {teamMember.lastActiveAt
                        ? formatDateTime(teamMember.lastActiveAt)
                        : <span className="tm-muted-text">No activity yet</span>}
                    </span>
                  </div>
                  {teamMember.suspendedAt && (
                    <>
                      <div className="tm-detail-row">
                        <span className="tm-detail-label">Suspended</span>
                        <span className="tm-detail-value tm-error-text">
                          {formatDateTime(teamMember.suspendedAt)}
                        </span>
                      </div>
                      <div className="tm-detail-row">
                        <span className="tm-detail-label">Reason</span>
                        <span className="tm-detail-value tm-error-text">
                          {teamMember.suspensionReason || 'N/A'}
                        </span>
                      </div>
                    </>
                  )}
                  {teamMember.archivedAt && (
                    <>
                      <div className="tm-detail-row">
                        <span className="tm-detail-label">Archived</span>
                        <span className="tm-detail-value tm-muted-text">
                          {formatDateTime(teamMember.archivedAt)}
                        </span>
                      </div>
                      <div className="tm-detail-row">
                        <span className="tm-detail-label">Reason</span>
                        <span className="tm-detail-value tm-muted-text">
                          {teamMember.archiveReason || 'N/A'}
                        </span>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </>
          )}

          {activeTab === 'merchant' && (
            <>
              <div className="tm-info-section">
                <h3 className="tm-info-section-title">
                  <Store size={15} />
                  Store Information
                </h3>
                <div className="tm-detail-list">
                  <div className="tm-detail-row">
                    <span className="tm-detail-label">Store Name</span>
                    <span className="tm-detail-value">{teamMember.store?.name || 'N/A'}</span>
                  </div>
                  <div className="tm-detail-row">
                    <span className="tm-detail-label">Store Slug</span>
                    <span className="tm-detail-value tm-monospace">{teamMember.store?.slug || 'N/A'}</span>
                  </div>
                </div>
              </div>
              <div className="tm-info-section">
                <h3 className="tm-info-section-title">
                  <User size={15} />
                  Store Owner
                </h3>
                <div className="tm-detail-list">
                  <div className="tm-detail-row">
                    <span className="tm-detail-label">Email</span>
                    <span className="tm-detail-value">{merchant?.email || 'N/A'}</span>
                  </div>
                  <div className="tm-detail-row">
                    <span className="tm-detail-label">Username</span>
                    <span className="tm-detail-value">@{merchant?.username || 'N/A'}</span>
                  </div>
                  <div className="tm-detail-row">
                    <span className="tm-detail-label">Tier</span>
                    <span className="tm-detail-value">{merchant?.merchantTier || 'N/A'}</span>
                  </div>
                </div>
              </div>
            </>
          )}

          {activeTab === 'activity' && (
            <>
              {activityLogs && activityLogs.length > 0 ? (
                <div className="tm-activity-list">
                  {activityLogs.map((log) => (
                    <div key={log.id} className="tm-activity-item">
                      <div className="tm-activity-dot" />
                      <div className="tm-activity-body">
                        <div className="tm-activity-header">
                          <span className="tm-activity-type">{log.action}</span>
                          <span className="tm-activity-date">{formatDateTime(log.createdAt)}</span>
                        </div>
                        <p className="tm-activity-desc">{log.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="tm-activity-empty">
                  <Activity size={42} />
                  <p>No activity logs recorded</p>
                </div>
              )}
            </>
          )}
        </div>

        {/* Actions */}
        <div className="tm-flyout-actions">
          {teamMember.status === 'ARCHIVED' ? (
            <button
              className="tm-action-btn tm-unarchive-btn"
              onClick={onUnarchive}
              disabled={actionLoading}
            >
              <RefreshCw size={16} />
              Unarchive
            </button>
          ) : (
            <>
              {teamMember.status === 'SUSPENDED' ? (
                <button
                  className="tm-action-btn tm-unsuspend-btn"
                  onClick={onUnsuspend}
                  disabled={actionLoading}
                >
                  <UserCheck size={16} />
                  Unsuspend
                </button>
              ) : teamMember.status === 'ACTIVE' && (
                <button
                  className="tm-action-btn tm-suspend-btn"
                  onClick={onSuspend}
                  disabled={actionLoading}
                >
                  <UserX size={16} />
                  Suspend
                </button>
              )}
              <button
                className="tm-action-btn tm-archive-btn"
                onClick={onArchive}
                disabled={actionLoading}
              >
                <Archive size={16} />
                Archive
              </button>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default TeamMembersManagement;
